import React, { useState, useEffect } from 'react';
import { collection, doc, setDoc, deleteDoc, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { DEFAULT_ROLE_PERMISSIONS, PERMISSION_GROUPS, ALL_PERMISSIONS } from '../utils/permissions';
import { usePermissions } from '../hooks/usePermissions';
import { PAGE_PERMISSIONS, FEATURE_PERMISSIONS } from '../utils/permissions';
import PermissionManager from '../components/forms/PermissionManager';
import Toast from '../components/common/Toast';
import { logEntityChanges } from '../utils/logger';

/**
 * Role Management Page
 * 
 * Admin-only page for creating and managing roles with permissions.
 * Allows creating custom roles and editing default role permissions.
 */
const RoleManagementPage = ({ user }) => {
  const { can, loading: permLoading } = usePermissions(user);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [viewingRoleUsers, setViewingRoleUsers] = useState(null);
  const [roleUsers, setRoleUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicatingRole, setDuplicatingRole] = useState(null);
  const [duplicateRoleName, setDuplicateRoleName] = useState('');
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [deletingRole, setDeletingRole] = useState(null);
  const [reassignToRole, setReassignToRole] = useState('');
  const [usersToReassign, setUsersToReassign] = useState([]);

  // Auto-sync admin role permissions on mount (only for users who can write)
  useEffect(() => {
    // Skip auto-sync for users who can't edit roles
    if (!can(FEATURE_PERMISSIONS.ROLE_EDIT)) return;
    
    const validateAndSyncAdminRole = async () => {
      try {
        const adminRoleDoc = await getDocs(
          collection(db, collectionPath('rolePermissions'))
        );
        
        const adminRole = adminRoleDoc.docs.find(doc => doc.id === 'admin');
        
        if (adminRole) {
          const currentPermissions = adminRole.data().permissions || [];
          const expectedPermissions = [...new Set(DEFAULT_ROLE_PERMISSIONS.admin)];
          
          // Check if permissions count matches
          if (currentPermissions.length !== expectedPermissions.length) {
            console.warn(
              `⚠️ Admin role permission mismatch detected: ${currentPermissions.length} in Firestore vs ${expectedPermissions.length} expected. Auto-syncing...`
            );
            
            // Auto-fix the mismatch
            await setDoc(
              doc(db, collectionPath('rolePermissions'), 'admin'),
              {
                permissions: expectedPermissions,
                description: 'Full system access with all permissions',
                isCustom: false,
                updatedAt: new Date().toISOString(),
                updatedBy: 'system',
                autoSynced: true,
              },
              { merge: true }
            );
            
            console.log(`✅ Admin role auto-synced to ${expectedPermissions.length} permissions`);
          }
        }
      } catch (error) {
        console.error('Error validating admin role:', error);
      }
    };

    validateAndSyncAdminRole();
  }, []);

  // Fetch roles from Firestore and calculate user counts
  useEffect(() => {
    const fetchRolesWithUserCounts = async () => {
      // Fetch all users to calculate counts
      const usersSnap = await getDocs(collection(db, collectionPath('users')));
      const users = usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Count users per role
      const userCountByRole = {};
      users.forEach(user => {
        const role = user.role || 'user';
        userCountByRole[role] = (userCountByRole[role] || 0) + 1;
      });
      
      return userCountByRole;
    };

    const unsub = onSnapshot(
      collection(db, collectionPath('rolePermissions')),
      async (snapshot) => {
        // Get user counts
        const userCountByRole = await fetchRolesWithUserCounts();
        
        const rolesList = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || getRoleDisplayName(doc.id),
          permissions: doc.data().permissions || [],
          description: doc.data().description || '',
          isCustom: doc.data().isCustom || false,
          userCount: userCountByRole[doc.id] || 0,
        }));
        
        // Create default roles in Firestore if they don't exist
        const existingRoleIds = rolesList.map(r => r.id);
        const missingDefaults = Object.keys(DEFAULT_ROLE_PERMISSIONS).filter(id => !existingRoleIds.includes(id));
        for (const roleId of missingDefaults) {
          const roleData = {
            name: getRoleDisplayName(roleId),
            permissions: DEFAULT_ROLE_PERMISSIONS[roleId],
            description: getDefaultRoleDescription(roleId),
            isCustom: false,
          };
          try {
            await setDoc(doc(db, collectionPath('rolePermissions'), roleId), roleData);
          } catch (err) {
            console.error(`Failed to seed default role ${roleId}:`, err);
          }
          rolesList.push({
            id: roleId,
            ...roleData,
            userCount: userCountByRole[roleId] || 0,
          });
        }
        
        setRoles(rolesList.sort((a, b) => a.name.localeCompare(b.name)));
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching roles:', error);
        setToast({ message: 'Failed to load roles', type: 'error' });
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  // Get default role descriptions
  const getDefaultRoleDescription = (roleId) => {
    const descriptions = {
      admin: 'Full system access with all permissions',
      adminViewOnly: 'Read-only admin — full data visibility, no modification rights',
      manager: 'Team management and full data access',
      marketing: 'Email marketing and contact management',
      user: 'Basic access to assigned records',
      disabled: 'Account disabled — cannot login or access any data',
    };
    return descriptions[roleId] || '';
  };

  // Get display name for role IDs (handles camelCase IDs)
  const getRoleDisplayName = (roleId) => {
    const displayNames = {
      admin: 'Admin',
      adminViewOnly: 'Admin - View Only',
      manager: 'Manager',
      marketing: 'Marketing',
      user: 'User',
      disabled: 'Disabled',
    };
    return displayNames[roleId] || roleId.charAt(0).toUpperCase() + roleId.slice(1);
  };

  // Create new custom role
  const handleCreateRole = async () => {
    if (!newRoleName.trim()) {
      setToast({ message: 'Role name is required', type: 'error' });
      return;
    }

    const roleId = newRoleName.toLowerCase().replace(/\s+/g, '_');
    
    // Check if role already exists
    if (roles.some(r => r.id === roleId)) {
      setToast({ message: 'A role with this name already exists', type: 'error' });
      return;
    }

    setSaving(true);
    try {
      const newRoleData = {
        name: newRoleName.trim(),
        permissions: [],
        description: newRoleDescription.trim(),
        isCustom: true,
        createdAt: new Date().toISOString(),
        createdBy: user.uid,
      };
      await setDoc(doc(db, collectionPath('rolePermissions'), roleId), newRoleData);

      // Audit log: role created
      await logEntityChanges({
        oldObj: {},
        newObj: newRoleData,
        fields: ['name', 'description', 'permissions', 'isCustom'],
        entityType: 'Role',
        entityId: roleId,
        user,
        action: 'Created Role',
        extraDetails: `Custom role "${newRoleName.trim()}" created`,
      });

      setToast({ message: 'Role created successfully', type: 'success' });
      setShowCreateModal(false);
      setNewRoleName('');
      setNewRoleDescription('');
    } catch (error) {
      console.error('Error creating role:', error);
      setToast({ message: 'Failed to create role', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Save role permissions
  const handleSavePermissions = async () => {
    if (!editingRole) return;

    setSaving(true);
    try {
      // Remove duplicate permissions before saving
      const uniquePermissions = [...new Set(editingRole.permissions)];
      
      // Log if duplicates were found and removed
      if (uniquePermissions.length !== editingRole.permissions.length) {
        console.warn(
          `⚠️ Removed ${editingRole.permissions.length - uniquePermissions.length} duplicate permissions from ${editingRole.name}`
        );
      }

      await setDoc(doc(db, collectionPath('rolePermissions'), editingRole.id),
        {
          name: editingRole.name,
          permissions: uniquePermissions,
          description: editingRole.description,
          isCustom: editingRole.isCustom,
          updatedAt: new Date().toISOString(),
          updatedBy: user.uid,
        },
        { merge: true }
      );

      // Audit log: role permission edits (diff of name / description / permissions)
      const previousRole = roles.find(r => r.id === editingRole.id) || {};
      await logEntityChanges({
        oldObj: {
          name: previousRole.name,
          description: previousRole.description,
          permissions: previousRole.permissions || [],
        },
        newObj: {
          name: editingRole.name,
          description: editingRole.description,
          permissions: uniquePermissions,
        },
        fields: ['name', 'description', 'permissions'],
        entityType: 'Role',
        entityId: editingRole.id,
        user,
        action: 'Updated Role Permissions',
        extraDetails: `Role "${editingRole.name}" permissions saved (${uniquePermissions.length} total)`,
      });

      setToast({ message: 'Permissions saved successfully', type: 'success' });
      setEditingRole(null);
      setSelectedRole(null);
    } catch (error) {
      console.error('Error saving permissions:', error);
      setToast({ message: 'Failed to save permissions', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // View users assigned to a role
  const handleViewUsers = async (role) => {
    setViewingRoleUsers(role);
    setLoadingUsers(true);
    setShowUsersModal(true);
    
    try {
      const usersSnap = await getDocs(collection(db, collectionPath('users')));
      const assignedUsers = usersSnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(u => u.role === role.id);
      
      setRoleUsers(assignedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      setToast({ message: 'Failed to load users', type: 'error' });
    } finally {
      setLoadingUsers(false);
    }
  };

  // Duplicate role
  const handleDuplicateRole = (role) => {
    setDuplicatingRole(role);
    setDuplicateRoleName(`${role.name} Copy`);
    setShowDuplicateModal(true);
  };

  const handleConfirmDuplicate = async () => {
    if (!duplicateRoleName.trim()) {
      setToast({ message: 'Role name is required', type: 'error' });
      return;
    }

    const roleId = duplicateRoleName.toLowerCase().replace(/\s+/g, '_');
    
    // Check if role ID already exists
    if (roles.some(r => r.id === roleId)) {
      setToast({ message: 'A role with this name already exists', type: 'error' });
      return;
    }

    setSaving(true);
    try {
      const duplicatedRoleData = {
        name: duplicateRoleName.trim(),
        description: `Copy of ${duplicatingRole.name}`,
        permissions: [...duplicatingRole.permissions], // Copy permissions
        isCustom: true,
        userCount: 0, // New role has no users
        createdAt: new Date().toISOString(),
        createdBy: user.uid,
      };
      await setDoc(doc(db, collectionPath('rolePermissions'), roleId), duplicatedRoleData);

      // Audit log: role duplicated
      await logEntityChanges({
        oldObj: {},
        newObj: duplicatedRoleData,
        fields: ['name', 'description', 'permissions', 'isCustom'],
        entityType: 'Role',
        entityId: roleId,
        user,
        action: 'Duplicated Role',
        extraDetails: `Role "${duplicateRoleName.trim()}" created as copy of "${duplicatingRole.name}" (${duplicatingRole.permissions.length} permissions copied)`,
      });

      setToast({ message: 'Role duplicated successfully', type: 'success' });
      setShowDuplicateModal(false);
      setDuplicatingRole(null);
      setDuplicateRoleName('');
    } catch (error) {
      console.error('Error duplicating role:', error);
      setToast({ message: 'Failed to duplicate role', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Delete custom role
  const handleDeleteRole = async (role) => {
    // Only protect admin and user roles from deletion
    const protectedRoles = ['admin', 'user'];
    if (protectedRoles.includes(role.id)) {
      setToast({ message: 'Cannot delete admin or user roles', type: 'error' });
      return;
    }

    // If role has users, show reassignment modal
    if (role.userCount > 0) {
      setLoadingUsers(true);
      try {
        const usersSnap = await getDocs(collection(db, collectionPath('users')));
        const assignedUsers = usersSnap.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(u => u.role === role.id);
        
        setUsersToReassign(assignedUsers);
        setDeletingRole(role);
        setReassignToRole('');
        setShowReassignModal(true);
      } catch (error) {
        console.error('Error fetching users:', error);
        setToast({ message: 'Failed to load users for reassignment', type: 'error' });
      } finally {
        setLoadingUsers(false);
      }
      return;
    }

    // No users assigned, proceed with direct deletion
    const confirmed = window.confirm(
      `Are you sure you want to delete the role "${role.name}"? This action cannot be undone.`
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      await deleteDoc(doc(db, collectionPath('rolePermissions'), role.id));

      // Audit log: role deleted (no users to reassign)
      await logEntityChanges({
        oldObj: {
          name: role.name,
          description: role.description,
          permissions: role.permissions || [],
          isCustom: role.isCustom,
        },
        newObj: {},
        fields: ['name', 'description', 'permissions', 'isCustom'],
        entityType: 'Role',
        entityId: role.id,
        user,
        action: 'Deleted Role',
        extraDetails: `Role "${role.name}" deleted (no users were assigned)`,
      });

      setToast({ message: 'Role deleted successfully', type: 'success' });
      if (selectedRole?.id === role.id) {
        setSelectedRole(null);
        setEditingRole(null);
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      setToast({ message: 'Failed to delete role', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Confirm reassignment and delete role
  const handleConfirmReassignment = async () => {
    if (!reassignToRole) {
      setToast({ message: 'Please select a role to reassign users to', type: 'error' });
      return;
    }

    setSaving(true);
    try {
      // Reassign all users
      const { updateDoc } = await import('firebase/firestore');
      for (const u of usersToReassign) {
        const previousRole = u.role;
        await updateDoc(doc(db, collectionPath('users'), u.id), {
          role: reassignToRole
        });

        // Audit log: user role reassignment (per user)
        await logEntityChanges({
          oldObj: { role: previousRole },
          newObj: { role: reassignToRole },
          fields: ['role'],
          entityType: 'User',
          entityId: u.id,
          user,
          action: 'Role Reassigned',
          extraDetails: `Role reassigned from "${previousRole}" to "${reassignToRole}" as part of deleting role "${deletingRole.name}"`,
        });
      }

      // Delete the role
      await deleteDoc(doc(db, collectionPath('rolePermissions'), deletingRole.id));

      // Audit log: role deleted after reassignment
      await logEntityChanges({
        oldObj: {
          name: deletingRole.name,
          description: deletingRole.description,
          permissions: deletingRole.permissions || [],
          isCustom: deletingRole.isCustom,
        },
        newObj: {},
        fields: ['name', 'description', 'permissions', 'isCustom'],
        entityType: 'Role',
        entityId: deletingRole.id,
        user,
        action: 'Deleted Role',
        extraDetails: `Role "${deletingRole.name}" deleted after reassigning ${usersToReassign.length} user(s) to "${reassignToRole}"`,
      });

      setToast({ 
        message: `Reassigned ${usersToReassign.length} users and deleted role successfully`, 
        type: 'success' 
      });
      
      setShowReassignModal(false);
      setDeletingRole(null);
      setUsersToReassign([]);
      setReassignToRole('');
      
      if (selectedRole?.id === deletingRole.id) {
        setSelectedRole(null);
        setEditingRole(null);
      }
    } catch (error) {
      console.error('Error reassigning users and deleting role:', error);
      setToast({ message: 'Failed to reassign users or delete role', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Handle permission change
  const handlePermissionChange = (permission, action) => {
    if (!editingRole) return;

    const updatedPermissions = [...editingRole.permissions];
    
    if (action === 'add' && !updatedPermissions.includes(permission)) {
      updatedPermissions.push(permission);
    } else if (action === 'remove') {
      const index = updatedPermissions.indexOf(permission);
      if (index > -1) updatedPermissions.splice(index, 1);
    }

    setEditingRole({
      ...editingRole,
      permissions: updatedPermissions,
    });
  };

  const handleBulkPermissionChange = (permissions, action) => {
    if (!editingRole) return;

    let updatedPermissions;
    if (action === 'add') {
      const existing = new Set(editingRole.permissions);
      updatedPermissions = [...editingRole.permissions, ...permissions.filter(p => !existing.has(p))];
    } else {
      const removeSet = new Set(permissions);
      updatedPermissions = editingRole.permissions.filter(p => !removeSet.has(p));
    }

    setEditingRole({ ...editingRole, permissions: updatedPermissions });
  };

  // Select role to view/edit
  const selectRole = (role) => {
    setSelectedRole(role);
    setEditingRole({ ...role });
  };

  // Check if user can access this page
  if (permLoading) {
    return (
      <main className="flex-grow p-6 overflow-y-auto">
        <div className="text-center py-8">Loading permissions...</div>
      </main>
    );
  }

  if (!can(PAGE_PERMISSIONS.ROLE_MANAGEMENT)) {
    return (
      <main className="flex-grow p-6 overflow-y-auto">
        <div className="text-center py-8 text-red-600">
          Access denied. You don't have permission to manage roles.
        </div>
      </main>
    );
  }

  return (
    <main className="flex-grow p-6 overflow-y-auto bg-gray-50">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Role Management</h2>
        <p className="text-gray-600 mt-1">
          Manage roles and their permissions. Changes apply immediately to all users with the role.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Roles List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Roles</h3>
              {can(FEATURE_PERMISSIONS.ROLE_CREATE) && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="text-sm px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  disabled={saving}
                >
                  + New Role
                </button>
              )}
            </div>

            <div className="divide-y max-h-[calc(100vh-250px)] overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-gray-500">Loading roles...</div>
              ) : roles.length === 0 ? (
                <div className="p-4 text-center text-gray-500">No roles found</div>
              ) : (
                roles.map((role) => (
                  <div
                    key={role.id}
                    className={`relative p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                      selectedRole?.id === role.id ? 'bg-indigo-50 border-l-4 border-indigo-600' : ''
                    }`}
                    onClick={() => selectRole(role)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{role.name}</h4>
                        <p className="text-sm text-gray-600 mt-1">{role.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                            {role.permissions.length} permissions
                          </span>
                          {role.isCustom && (
                            <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                              Custom
                            </span>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewUsers(role);
                            }}
                            className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors flex items-center gap-1"
                          >
                            <span>👤</span>
                            <span>{role.userCount || 0} users</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Action buttons */}
                    {can(FEATURE_PERMISSIONS.ROLE_EDIT) && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-gray-200">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicateRole(role);
                        }}
                        className="text-xs px-3 py-1.5 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors font-medium"
                      >
                        📋 Duplicate
                      </button>
                      {!['admin', 'user'].includes(role.id) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRole(role);
                          }}
                          className="text-xs px-3 py-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                        >
                          🗑️ Delete
                        </button>
                      )}
                    </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Permission Editor */}
        <div className="lg:col-span-2">
          {!selectedRole ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              <svg
                className="w-16 h-16 mx-auto mb-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <p className="text-lg">Select a role to view and edit permissions</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow">
              {/* Role Header */}
              <div className="p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">{selectedRole.name}</h3>
                {editingRole && can(FEATURE_PERMISSIONS.ROLE_EDIT) ? (
                  <textarea
                    className="w-full mt-2 text-sm border border-gray-300 rounded p-2 focus:ring-2 focus:ring-indigo-500"
                    placeholder="Add a role description..."
                    value={editingRole.description}
                    onChange={(e) => setEditingRole({ ...editingRole, description: e.target.value })}
                    rows={2}
                    maxLength={200}
                  />
                ) : (
                  <p className="text-sm text-gray-600 mt-1">{selectedRole.description || 'No description'}</p>
                )}
                {selectedRole.userCount > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    👤 {selectedRole.userCount} users assigned to this role
                  </p>
                )}
              </div>

              {/* Permission Manager */}
              {editingRole && (
                <>
                  <PermissionManager
                    rolePermissions={editingRole.permissions}
                    onPermissionChange={handlePermissionChange}
                    onBulkPermissionChange={handleBulkPermissionChange}
                    isAdmin={can(FEATURE_PERMISSIONS.ROLE_EDIT)}
                    readOnly={!can(FEATURE_PERMISSIONS.ROLE_EDIT)}
                  />

                  {/* Save/Cancel Buttons */}
                  {can(FEATURE_PERMISSIONS.ROLE_EDIT) && (
                    <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setEditingRole(null);
                          setSelectedRole(null);
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSavePermissions}
                        className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save Permissions'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Role Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Create New Role</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Role Name<span className="text-red-600">*</span></label>
                <input
                  type="text"
                  className="w-full border p-2 rounded"
                  placeholder="e.g., Sales Manager"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  maxLength={50}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  className="w-full border p-2 rounded"
                  placeholder="Describe the role's purpose and responsibilities"
                  value={newRoleDescription}
                  onChange={(e) => setNewRoleDescription(e.target.value)}
                  rows={3}
                  maxLength={200}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewRoleName('');
                  setNewRoleDescription('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateRole}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                disabled={saving || !newRoleName.trim()}
              >
                {saving ? 'Creating...' : 'Create Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Users Modal */}
      {showUsersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold">
                  Users with role: {viewingRoleUsers?.name}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {roleUsers.length} users assigned to this role
                </p>
              </div>
              <button
                onClick={() => {
                  setShowUsersModal(false);
                  setViewingRoleUsers(null);
                  setRoleUsers([]);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {loadingUsers ? (
              <div className="text-center py-8 text-gray-500">Loading users...</div>
            ) : roleUsers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No users found</div>
            ) : (
              <div className="space-y-2">
                {roleUsers.map(user => (
                  <div key={user.id} className="p-3 border rounded hover:bg-gray-50">
                    <div className="font-medium">{user.firstName} {user.lastName}</div>
                    <div className="text-sm text-gray-600">{user.email}</div>
                    {user.phone && (
                      <div className="text-xs text-gray-500 mt-1">
                        Phone: {user.phone}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowUsersModal(false);
                  setViewingRoleUsers(null);
                  setRoleUsers([]);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Role Modal */}
      {showDuplicateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              Duplicate Role: {duplicatingRole?.name}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  New Role Name<span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  className="w-full border p-2 rounded"
                  placeholder="Enter new role name"
                  value={duplicateRoleName}
                  onChange={(e) => setDuplicateRoleName(e.target.value)}
                  maxLength={50}
                />
              </div>
              
              <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
                <p className="font-medium mb-1">ℹ️ What will be copied:</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li>All {duplicatingRole?.permissions?.length || 0} permissions</li>
                  <li>Description (you can edit later)</li>
                </ul>
                <p className="mt-2">User assignments to this role will NOT be copied.</p>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowDuplicateModal(false);
                  setDuplicatingRole(null);
                  setDuplicateRoleName('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDuplicate}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                disabled={saving || !duplicateRoleName.trim()}
              >
                {saving ? 'Duplicating...' : 'Duplicate Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign Users Modal */}
      {showReassignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4 text-red-700">
              ⚠️ Reassign Users Before Deleting Role
            </h3>

            <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
              <p className="text-sm text-red-800">
                Cannot delete role <strong>{deletingRole?.name}</strong> because {usersToReassign.length} users are assigned to it.
              </p>
              <p className="text-sm text-red-700 mt-2">
                Please select a new role to reassign these users to:
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Reassign to Role<span className="text-red-600">*</span>
              </label>
              <select
                value={reassignToRole}
                onChange={(e) => setReassignToRole(e.target.value)}
                className="w-full border p-2 rounded bg-white"
              >
                <option value="">-- Select a role --</option>
                {roles
                  .filter(r => r.id !== deletingRole?.id) // Exclude the role being deleted
                  .map(r => (
                    <option key={r.id} value={r.id}>
                      {r.name} ({r.permissions.length} permissions)
                    </option>
                  ))}
              </select>
            </div>

            <div className="mb-4">
              <p className="text-sm font-medium mb-2">
                Users to be reassigned ({usersToReassign.length}):
              </p>
              <div className="max-h-48 overflow-y-auto border rounded p-2 space-y-2">
                {usersToReassign.map(user => (
                  <div key={user.id} className="text-sm p-2 bg-gray-50 rounded">
                    {user.firstName} {user.lastName} ({user.email})
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowReassignModal(false);
                  setDeletingRole(null);
                  setUsersToReassign([]);
                  setReassignToRole('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReassignment}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                disabled={saving || !reassignToRole}
              >
                {saving ? 'Reassigning...' : 'Reassign & Delete Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default RoleManagementPage;
